import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, ChevronLeft, ChevronRight, RefreshCw, Info, Save, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_WINDOW = 7; // one week visible at a time
const HOUR_HEIGHT_PX = 36; // vertical timeline (per hour) — slightly tighter for the wider window
const DAY_WIDTH_PX = 180;
const DAY_START_HOUR = 5;   // 05:00 IST — early-morning study sessions
const DAY_END_HOUR = 24;    // 24:00 (midnight) — covers late-night sessions
const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR;

const IST_TZ = 'Asia/Kolkata';

const fmtDay = (dt) => new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: '2-digit', month: 'short', timeZone: IST_TZ }).format(dt);
const fmtTime = (iso) => new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: IST_TZ }).format(new Date(iso));

// Convert an ISO timestamp to hour-of-day (float) in IST (e.g. 14:30 → 14.5)
const istHour = (iso) => {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: IST_TZ }).formatToParts(d);
  const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
  return h + m / 60;
};

const istDateKey = (iso) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: IST_TZ }).format(new Date(iso));

const statusColors = {
  scheduled: 'bg-blue-500/85 border-blue-700 text-white',
  live: 'bg-emerald-500/90 border-emerald-700 text-white',
  completed: 'bg-gray-400/85 border-gray-600 text-white',
};

const LicenseSchedulePanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [licenses, setLicenses] = useState([]);
  const [bufferMinutes, setBufferMinutes] = useState(15);
  const [bufferDraft, setBufferDraft] = useState(15);
  const [bufferDirty, setBufferDirty] = useState(false);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < DAYS_WINDOW; i++) {
      const d = new Date(rangeStart);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [rangeStart]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const startISO = rangeStart.toISOString();
      const [schedResp, bufResp] = await Promise.all([
        axios.get(`${API}/admin/zoom-accounts/schedule`, {
          params: { days: DAYS_WINDOW, start_date: startISO },
        }),
        axios.get(`${API}/admin/zoom-accounts/buffer-setting`),
      ]);
      setLicenses(schedResp.data?.licenses || []);
      const bm = bufResp.data?.zoom_buffer_minutes ?? 15;
      setBufferMinutes(bm);
      setBufferDraft(bm);
      setBufferDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [rangeStart]);

  useEffect(() => { load(); }, [load]);

  const shiftRange = (delta) => {
    const d = new Date(rangeStart);
    d.setDate(d.getDate() + delta * DAYS_WINDOW);
    setRangeStart(d);
  };

  const saveBuffer = async () => {
    try {
      setSaving(true);
      const resp = await axios.put(`${API}/admin/zoom-accounts/buffer-setting`, {
        zoom_buffer_minutes: bufferDraft,
      });
      setBufferMinutes(resp.data.zoom_buffer_minutes);
      setBufferDirty(false);
      toast.success(`Buffer set to ${resp.data.zoom_buffer_minutes} min`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save buffer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="license-schedule-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-[#0A7EA4]">
              <Clock className="w-5 h-5" />
              License Schedule
            </CardTitle>
            <CardDescription>
              See which Zoom license is booked at what time. New classes cannot overlap existing bookings on the same license (a {bufferMinutes}-min buffer is enforced).
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1" data-testid="buffer-input-group">
              <Label className="text-xs text-gray-600 whitespace-nowrap">Buffer (min)</Label>
              <Input
                type="number"
                min={0}
                max={120}
                value={bufferDraft}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(120, parseInt(e.target.value, 10) || 0));
                  setBufferDraft(v);
                  setBufferDirty(v !== bufferMinutes);
                }}
                className="h-8 w-16 tabular-nums text-sm"
                data-testid="buffer-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={saveBuffer}
                disabled={!bufferDirty || saving}
                className="h-8 px-2"
                data-testid="buffer-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={load} className="h-8" data-testid="schedule-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Range navigator */}
        <div className="flex items-center justify-between mb-4 border-b pb-3">
          <Button variant="ghost" size="sm" onClick={() => shiftRange(-1)} data-testid="schedule-prev-week">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous week
          </Button>
          <span className="text-sm font-medium text-gray-700 tabular-nums" data-testid="schedule-range-label">
            {fmtDay(days[0])} — {fmtDay(days[days.length - 1])}
          </span>
          <Button variant="ghost" size="sm" onClick={() => shiftRange(1)} data-testid="schedule-next-week">
            Next week <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading schedule...
          </div>
        ) : licenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <Info className="w-5 h-5 mx-auto mb-2 text-gray-400" />
            No Zoom licenses configured yet. Add one in the &ldquo;Zoom Licenses&rdquo; tab.
          </div>
        ) : (
          <div className="space-y-6">
            {licenses.map((lic) => (
              <LicenseTimeline key={lic.id} license={lic} days={days} />
            ))}

            {/* Legend */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-500 pt-2 border-t">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-blue-500/85 border border-blue-700"></span>
                Scheduled
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/90 border border-emerald-700"></span>
                Live
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-gray-400/85 border border-gray-600"></span>
                Completed
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm border-2 border-dashed border-amber-500"></span>
                Buffer ({bufferMinutes}m)
              </span>
              <span className="text-gray-400">All times in IST · Showing {String(DAY_START_HOUR).padStart(2,'0')}:00–{DAY_END_HOUR === 24 ? '23:59' : String(DAY_END_HOUR).padStart(2,'0') + ':00'}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const LicenseTimeline = ({ license, days }) => {
  // Group bookings by day (IST)
  const bookingsByDay = useMemo(() => {
    const map = {};
    (license.bookings || []).forEach((b) => {
      const key = istDateKey(b.start_time);
      (map[key] ||= []).push(b);
    });
    return map;
  }, [license.bookings]);

  const totalBookings = (license.bookings || []).length;

  return (
    <div data-testid={`license-row-${license.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-gray-800">
            {license.label || 'Unlabelled license'}
            {!license.is_active && <span className="ml-2 text-xs font-normal text-red-500">(inactive)</span>}
          </p>
          <p className="text-xs text-gray-400">
            {totalBookings} booking{totalBookings === 1 ? '' : 's'} in view
          </p>
        </div>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <div className="flex">
          {/* Hour ruler */}
          <div className="shrink-0 border-r bg-gray-50 sticky left-0 z-10" style={{ width: 44 }}>
            <div style={{ height: 28 }} className="border-b" />
            {Array.from({ length: TOTAL_HOURS }).map((_, i) => (
              <div
                key={i}
                className="text-[10px] text-gray-400 tabular-nums text-right pr-1 border-b"
                style={{ height: HOUR_HEIGHT_PX }}
              >
                {String(DAY_START_HOUR + i).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {/* Days */}
          {days.map((day) => {
            const dayKey = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: IST_TZ }).format(day);
            const dayBookings = bookingsByDay[dayKey] || [];
            return (
              <div key={dayKey} className="shrink-0 border-r relative" style={{ width: DAY_WIDTH_PX }}>
                <div className="text-[11px] font-medium text-center py-1 border-b bg-gray-50 text-gray-600" style={{ height: 28 }}>
                  {fmtDay(day)}
                </div>
                {/* Hour cells (grid) */}
                <div className="relative" style={{ height: HOUR_HEIGHT_PX * TOTAL_HOURS }}>
                  {Array.from({ length: TOTAL_HOURS }).map((_, i) => (
                    <div
                      key={i}
                      className="border-b border-gray-100"
                      style={{ height: HOUR_HEIGHT_PX }}
                    />
                  ))}
                  {/* Booking blocks */}
                  {dayBookings.map((b) => {
                    const startH = istHour(b.start_time);
                    const durMin = b.duration_minutes || 60;
                    const endH = startH + durMin / 60;
                    // Clip to visible range
                    const clippedStart = Math.max(DAY_START_HOUR, startH);
                    const clippedEnd = Math.min(DAY_END_HOUR, endH);
                    if (clippedEnd <= clippedStart) return null;
                    const top = (clippedStart - DAY_START_HOUR) * HOUR_HEIGHT_PX;
                    const height = (clippedEnd - clippedStart) * HOUR_HEIGHT_PX;
                    const color = statusColors[b.status] || statusColors.scheduled;
                    return (
                      <div
                        key={b.id}
                        className={`absolute left-1 right-1 rounded-md border shadow-sm px-1.5 py-1 overflow-hidden ${color}`}
                        style={{ top, height: Math.max(20, height) }}
                        title={`${b.title} · ${fmtTime(b.start_time)}–${fmtTime(b.end_time)} · ${b.host || ''} (${b.status})`}
                        data-testid={`booking-block-${b.id}`}
                      >
                        <p className="text-[10px] leading-tight font-medium truncate">{b.title || 'Untitled'}</p>
                        <p className="text-[9px] leading-tight opacity-90 tabular-nums truncate">
                          {fmtTime(b.start_time)} · {durMin}m
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LicenseSchedulePanel;
